import { StepType } from '@reactour/tour';
import TourStepContent from '../TourStepContent';
import { TourConfig } from '../tourTypes';
import { blockAllNoDim, blockInteraction, sidebarMenuStep } from '../stepStyles';
import SandboxTourNote from '../SandboxTourNote';

// Step indices for the CANpark "Register a plate" tour. The gate steps
// (OPEN_CANPARK, MANAGE_SITES_ITEM, REGISTER_BUTTON, SAVE) auto-advance via
// useSidebarTourControl + useRegisterPlateTourSync; PLATE and VALIDITY hold their
// Next button until the user fills the fields (tourGate); ADD_MORE is a blocked,
// Next-driven info step. The save is sandboxed — no real plates are registered.
export const REGISTER_PLATE_STEP = {
	WELCOME: 0,
	OPEN_CANPARK: 1,
	MANAGE_SITES_ITEM: 2,
	REGISTER_BUTTON: 3,
	PLATE: 4,
	ADD_MORE: 5,
	VALIDITY: 6,
	SAVE: 7,
	FINISH: 8,
} as const;

// The registration form is a large, scrollable MUI Dialog; re-measure as it
// mutates/resizes so the popover stays anchored to its fields.
const registerDialogObservers = {
	mutationObservables: ['.MuiDialog-paper'],
	resizeObservables: ['.MuiDialog-paper'],
};

const steps: StepType[] = [
	{
		selector: 'body',
		position: 'center',
		styles: blockAllNoDim,
		content: (
			<TourStepContent
				title="Register a plate 🚗"
				body={
					<>
						Let’s register a licence plate for a parking site so trusted,
						frequent visitors can come and go without getting a record.
						<SandboxTourNote>
							<strong>Note:</strong> This is a sandbox walkthrough. No
							plates will actually be registered.
						</SandboxTourNote>
					</>
				}
			/>
		),
	},
	{
		selector: '[data-tour="canpark-menu-group"]',
		position: 'right',
		...sidebarMenuStep('[data-tour="canpark-menu-group"]'),
		content: (
			<TourStepContent
				awaitAction
				tailPosition="right"
				body="Open the CANpark menu — click it to expand."
			/>
		),
	},
	{
		selector: '[data-tour="manage-sites-menu-item"]',
		position: 'right',
		...sidebarMenuStep('[data-tour="manage-sites-menu-item"]'),
		content: (
			<TourStepContent
				awaitAction
				hideBack
				tailPosition="right"
				body="Now click Manage Sites."
			/>
		),
	},
	{
		selector: '[data-tour="register-plates-button"]',
		// Centre the popover under the button and point the tail up at it — the
		// button sits low in the card, so there's room below and none above.
		position: ({ left, right, bottom }, prevRect) => [
			(left + right) / 2 - prevRect.width / 2,
			bottom + 12,
		],
		content: (
			<TourStepContent
				awaitAction
				hideBack
				tailPosition="bottom"
				body="Click “Register Plates” on a parking site to register a licence plate for it."
			/>
		),
	},
	{
		selector: '[data-tour="register-plate-fields"]',
		position: 'bottom',
		content: (
			<TourStepContent
				requires="register-plate-info"
				hideBack
				tailPosition="bottom"
				body="Enter the licence plate and pick its state/province to continue."
			/>
		),
		...registerDialogObservers,
	},
	{
		// Info-only: blocked so clicking "Add License Plate" (which appends another
		// row and reflows the dialog) can't break the flow.
		selector: '[data-tour="register-plate-add-more"]',
		// reactour's default 'right' aligns the popover's top near the target's top,
		// so over a short button the centred tail sits too low. Centre it vertically
		// on the button instead — (top + bottom) / 2 is the button's mid-line (the
		// coords' own width/height are the POPOVER's, so derive target height from the
		// rect edges); right + 10 matches the default horizontal offset.
		position: ({ top, right, bottom }, prevRect) => [
			right + 10,
			(top + bottom) / 2 - prevRect.height / 2,
		],
		styles: blockInteraction,
		content: (
			<TourStepContent
				tailPosition="right"
				body="Registering several plates? Click “Add License Plate” to add more rows — we’ll keep it to one for now."
			/>
		),
		...registerDialogObservers,
	},
	{
		selector: '[data-tour="register-plate-validity"]',
		position: 'top',
		content: (
			<TourStepContent
				requires="register-plate-validity"
				tailPosition="top"
				body="Set how long the registration is valid — pick a “Valid From” and a “Valid Until” date to continue."
			/>
		),
		...registerDialogObservers,
	},
	{
		selector: '[data-tour="register-plate-save"]',
		position: 'top',
		content: (
			<TourStepContent
				awaitAction
				tailPosition="top"
				body="Click “Save” to register the plate."
			/>
		),
		...registerDialogObservers,
	},
	{
		// FINISH — reached after the sandboxed save closes the dialog and returns to
		// Manage Sites. No real plates were registered.
		selector: 'body',
		position: 'center',
		styles: blockAllNoDim,
		content: (
			<TourStepContent
				finalStep
				title="Plate registered! 🎉"
				body="This is how you register plates to a parking lot, allowing your frequent visitors to come in without any issues."
			/>
		),
	},
];

export const canparkRegisterPlateTour: TourConfig = {
	steps,
	startRoute: '/',
	allowedRoutes: ['/', '/canpark-admin'],
};
